/**
 * Shared lock formatting helpers for the consumer (new-ui) lock screens.
 * Centralised here so the duration/date formatting isn't duplicated across
 * LockCard, the ended-rental summary, etc.
 */

const pad = (n: number) => String(n).padStart(2, "0");

/**
 * Duration between `start` and `end` using the active locale's unit labels.
 * When `end` is omitted (or null) the duration is computed up to "now" — useful
 * for an ongoing rental. Returns "-" when `start` is missing.
 */
export const formatLockDuration = (start: string | null | undefined, end?: string | null, locale?: string): string => {
	if (!start) return "-";

	const startMs = new Date(start).getTime();
	const endMs = end ? new Date(end).getTime() : Date.now();
	const diffMs = Math.max(0, endMs - startMs);

	const totalMinutes = Math.floor(diffMs / 60000);
	const days = Math.floor(totalMinutes / (60 * 24));
	const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
	const minutes = totalMinutes % 60;

	const isGerman = locale?.toLowerCase().startsWith("de");
	const parts: string[] = [];
	if (days > 0) parts.push(isGerman ? `${days} ${days === 1 ? "Tag" : "Tage"}` : `${days}d`);
	if (days > 0 || hours > 0) parts.push(isGerman ? `${pad(hours)} Std.` : `${pad(hours)}h`);
	parts.push(isGerman ? `${pad(minutes)} Min.` : `${pad(minutes)}min`);
	return parts.join(" ");
};

/** Date/time formatted like the design: "10.12.25, 8:30". */
export const formatLockDateTime = (value: string): string => {
	const date = new Date(value);
	const dd = pad(date.getDate());
	const mm = pad(date.getMonth() + 1);
	const yy = String(date.getFullYear()).slice(-2);
	const hh = date.getHours();
	const min = pad(date.getMinutes());
	return `${dd}.${mm}.${yy}, ${hh}:${min}`;
};
